import { useCallback, useMemo, useState } from 'react'

import { TLiveEventsCommonFilters } from '../types/LiveEventsCommonFilters'
import { TrackedGame } from '../types/TrackedGame'
import { useTrackedGamesCurrencies } from './useTrackedGamesCurrencies'
import { useFilteredTrackedGamesEvents } from './useTrackedGamesEvents'

type UseLiveEventsFilteredDataParams = {
  selectedGames: TrackedGame[]
  trackedEvents: any
  commonFilters: TLiveEventsCommonFilters
  eventNameSearchValue: string
  performanceEffectThreshold?: number
  performanceChangesBy?: any
}

export const useLiveEventsFilteredData = ({
  selectedGames,
  trackedEvents,
  commonFilters,
  performanceEffectThreshold = 0,
  performanceChangesBy = 'Peak_7_days',
  eventNameSearchValue: initialEventNameSearchValue,
}: UseLiveEventsFilteredDataParams) => {
  const [eventNameSearchValue, setEventNameSearchValue] = useState<string>(initialEventNameSearchValue)

  const selectedGameIds = useMemo(() => selectedGames.map((trackedGame) => trackedGame.game.id), [selectedGames])

  const selectedGameCurrencies = useTrackedGamesCurrencies({ gameIds: selectedGameIds, allTrackedGames: selectedGames })

  // Convert common filters to the format expected by useFilteredTrackedGamesEvents
  const filters = useMemo(
    () => ({
      selectedLiveEventTags: commonFilters.liveEventTags,
      selectedLiveEventSecondaryTags: commonFilters.liveEventSecondaryTags,
      selectedLiveEventsDurationIdsMap: commonFilters.liveEventDurations,
      selectedLiveEventsAppearanceIdsMap: commonFilters.liveEventAppearances,
      selectedLiveEventsPricePointIdsMap: commonFilters.liveEventPricePoints,
      selectedMotivationsIdsMap: commonFilters.motivations,
      selectedArchetypesIdsMap: commonFilters.archetypes,
      performanceEffectThreshold,
      performanceChangesBy,
      eventNameSearchValue,
    }),
    [
      commonFilters.liveEventTags,
      commonFilters.liveEventSecondaryTags,
      commonFilters.liveEventDurations,
      commonFilters.liveEventAppearances,
      commonFilters.liveEventPricePoints,
      commonFilters.motivations,
      commonFilters.archetypes,
      performanceEffectThreshold,
      performanceChangesBy,
      eventNameSearchValue,
    ]
  )

  const filteredTrackingEventsByGame = useFilteredTrackedGamesEvents({
    currencies: selectedGameCurrencies,
    data: trackedEvents,
    ...filters,
  })

  const handleEventNameSearchValueChange = useCallback((value: string) => {
    setEventNameSearchValue(value)
  }, [])

  return {
    filteredTrackingEventsByGame,
    eventNameSearchValue,
    handleEventNameSearchValueChange,
  }
}
